import { ThunkAction, UnknownAction } from "@reduxjs/toolkit"
import { QueryStatus } from "@reduxjs/toolkit/query"
import { isAxiosError } from "axios"
import { cellsSocket } from "@/store/apis/cellsAPI/cells.socket"
import { cellKeyOf } from "@/store/apis/mobsAPI/cellReads"
import { mobsAPI } from "@/store/apis/mobsAPI/mobs.api"
import { IGetCellMobsReq } from "@/store/apis/mobsAPI/mobs.types"

export type IHealReadVerdict = "ok" | "retry" | "stop"

export interface IHealReadResult {
  status: QueryStatus
  error?: unknown
  data?: unknown
}

export interface ICellHealIo {
  readInFlight(cell: IGetCellMobsReq): Promise<void> | undefined
  entryExists(cell: IGetCellMobsReq): boolean
  channelStarted(): boolean
  sendRead(cell: IGetCellMobsReq): Promise<IHealReadVerdict>
}

export interface ICellHealerDeps {
  nowMs(): number
  schedule(run: () => void, delayMs: number): () => void
}

export interface ICellHealer {
  request(cell: IGetCellMobsReq, delayMs: number, io: ICellHealIo): void
}

const HEAL_RETRY_DELAYS_MS = [1000, 2000, 4000]

interface IHealSlot {
  io: ICellHealIo
  dueAtMs: number | null
  cancel: (() => void) | null
  sending: boolean
  againAtMs: number | null
  retries: number
}

/** One pending heal per cell key; it waits for a read in flight, then checks the entry and the channel right before it sends.
 * A trigger while a read is awaited adds one read after it, one during a retry backoff only brings the retry forward; either way that read gets its own retries. */
export const createCellHealer = (deps: ICellHealerDeps): ICellHealer => {
  const slots = new Map<string, IHealSlot>()

  const settle = (
    key: string,
    cell: IGetCellMobsReq,
    slot: IHealSlot
  ): void => {
    slot.sending = false
    slot.retries = 0
    if (slot.againAtMs === null) {
      slots.delete(key)
      return
    }
    const dueAtMs = slot.againAtMs
    slot.againAtMs = null
    arm(key, cell, slot, dueAtMs)
  }

  const backOff = (
    key: string,
    cell: IGetCellMobsReq,
    slot: IHealSlot
  ): void => {
    slot.sending = false
    const retryAtMs = deps.nowMs() + HEAL_RETRY_DELAYS_MS[slot.retries]
    slot.retries = slot.againAtMs === null ? slot.retries + 1 : 0
    const dueAtMs =
      slot.againAtMs === null ? retryAtMs : Math.min(slot.againAtMs, retryAtMs)
    slot.againAtMs = null
    arm(key, cell, slot, dueAtMs)
  }

  const attempt = async (
    key: string,
    cell: IGetCellMobsReq,
    slot: IHealSlot
  ): Promise<void> => {
    await slot.io.readInFlight(cell)
    if (!slot.io.entryExists(cell) || !slot.io.channelStarted()) {
      settle(key, cell, slot)
      return
    }
    const verdict = await slot.io.sendRead(cell)
    if (verdict === "retry" && slot.retries < HEAL_RETRY_DELAYS_MS.length) {
      backOff(key, cell, slot)
      return
    }
    settle(key, cell, slot)
  }

  const send = (key: string, cell: IGetCellMobsReq, slot: IHealSlot): void => {
    slot.dueAtMs = null
    slot.cancel = null
    slot.sending = true
    void attempt(key, cell, slot)
  }

  const arm = (
    key: string,
    cell: IGetCellMobsReq,
    slot: IHealSlot,
    dueAtMs: number
  ): void => {
    if (slot.dueAtMs !== null && slot.dueAtMs <= dueAtMs) {
      return
    }
    slot.cancel?.()
    const waitMs = dueAtMs - deps.nowMs()
    if (waitMs <= 0) {
      send(key, cell, slot)
      return
    }
    slot.dueAtMs = dueAtMs
    slot.cancel = deps.schedule(() => send(key, cell, slot), waitMs)
  }

  return {
    request(cell, delayMs, io) {
      const key = cellKeyOf(cell)
      const slot = slots.get(key) ?? {
        io,
        dueAtMs: null,
        cancel: null,
        sending: false,
        againAtMs: null,
        retries: 0
      }
      slots.set(key, slot)
      slot.io = io
      const dueAtMs = deps.nowMs() + delayMs
      if (slot.sending) {
        slot.againAtMs =
          slot.againAtMs === null ? dueAtMs : Math.min(slot.againAtMs, dueAtMs)
        return
      }
      slot.retries = 0
      arm(key, cell, slot, dueAtMs)
    }
  }
}

/** Branches on the status and the error only: a failed re-read still carries the entry's old rows in `data`. */
export const healReadVerdict = (result: IHealReadResult): IHealReadVerdict => {
  if (result.status === QueryStatus.fulfilled) {
    return "ok"
  }
  if (result.status !== QueryStatus.rejected || !isAxiosError(result.error)) {
    return "stop"
  }
  const status = result.error.response?.status
  return status === undefined || status >= 500 ? "retry" : "stop"
}

export const cellHealer = createCellHealer({
  nowMs: () => Date.now(),
  schedule: (run, delayMs) => {
    const timer = setTimeout(run, delayMs)
    return () => clearTimeout(timer)
  }
})

type ICellHealState = Parameters<
  ReturnType<typeof mobsAPI.endpoints.getCellMobs.select>
>[0]

export const requestCellHeal =
  (
    cell: IGetCellMobsReq,
    delayMs: number
  ): ThunkAction<void, unknown, unknown, UnknownAction> =>
  (dispatch, getState) => {
    cellHealer.request(cell, delayMs, {
      readInFlight: (target) =>
        dispatch(
          mobsAPI.util.getRunningQueryThunk("getCellMobs", target)
        )?.then(() => undefined),
      entryExists: (target) =>
        mobsAPI.endpoints.getCellMobs.select(target)(
          getState() as ICellHealState
        ).status !== QueryStatus.uninitialized,
      channelStarted: () => cellsSocket.isStarted(),
      sendRead: async (target) =>
        healReadVerdict(
          await dispatch(
            mobsAPI.endpoints.getCellMobs.initiate(target, {
              forceRefetch: true,
              subscribe: false
            })
          )
        )
    })
  }
